import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { Loader, SectionHeading, EmptyState } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { fmtDate } from "../../lib/format";
import { Wrench } from "lucide-react";

const STATUSES = ["Submitted", "Under Review", "Additional Information Needed", "Approved", "Not Covered", "Contractor Assigned", "Scheduling", "Scheduled", "In Progress", "Work Completed", "Homeowner Confirmation", "Closed"];

export default function AdminService() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") || "All");
  const { data: srs = [], isLoading } = useQuery({ queryKey: ["admin-service"], queryFn: async () => (await api.get("/service-requests")).data });

  const filtered = srs.filter((s) => status === "All" || s.status === status);

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-6 arq-fade-up">
      <SectionHeading overline="Service" title="Service Requests">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" data-testid="sr-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="All">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </SectionHeading>

      {filtered.length === 0 ? <EmptyState icon={Wrench} title="No service requests" /> : (
        <div className="bg-arq-card border border-border rounded-md overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-arq-surface/60 text-left"><tr>{["#", "Category", "Homeowner", "Property", "Priority", "Submitted", "Status"].map((h) => <th key={h} className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-border">
              {filtered.map((s) => (
                <tr key={s.id} onClick={() => navigate(`/admin/service/${s.id}`)} data-testid={`admin-sr-${s.id}`} className="hover:bg-arq-surface/40 cursor-pointer">
                  <td className="px-4 py-3 font-mono text-arq-accent">#{s.number}</td>
                  <td className="px-4 py-3 font-medium text-arq-ink">{s.category}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.homeowner_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.property?.residence_id ? `#${s.property.residence_id}` : "—"}</td>
                  <td className="px-4 py-3"><StatusPill status={s.priority} /></td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(s.created_at)}</td>
                  <td className="px-4 py-3"><StatusPill status={s.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
